// C:\Users\USER\bookfete\frontend\src\App.js
import BookipixLoader from './components/book/atelier/BookipixLoader';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { supabase } from './services/supabaseClient';
import { linkAnonymousBooksAfterLogin } from './services/anonymousSession';
import { initLanguagePreferenceSync } from './services/languagePreference';
import { detectCountryOnFirstVisit } from './services/countryPreference';
import { fetchDetectedCountry } from './services/appStatusApi';
import AdminApp from './components/admin/shell/AdminApp';
import SiteModeGate from './components/common/SiteModeGate';


import './styles/luxe-theme.css';
import './styles/wizard.css';

// Layout
import Layout from './components/layout/Layout';

// Pages - VERSIONS LUXE
import HomePageLuxe from './components/home/HomePageLuxe';
import HowItWorksLuxe from './components/home/HowItWorksLuxe';
import FaqLuxe from './components/home/FaqLuxe';
import LoginLuxe from './components/auth/LoginLuxe';
import RegisterLuxe from './components/auth/RegisterLuxe';
import ForgotPasswordLuxe from './components/auth/ForgotPasswordLuxe';
import ResetPasswordLuxe from './components/auth/ResetPasswordLuxe';
import MesLivresLuxe from './components/auth/MesLivresLuxe';
import CreateBookSansIA from './components/create-book/CreateBookSansIA';
import DashboardGeneralLuxe from './components/dashboard/DashboardGeneralLuxe';
import BookPageLuxe  from './components/book/BookPageLuxe';
import BookComposeLuxe from './components/book/BookComposeLuxe';
import BookAtelierLuxe from './components/book/atelier/BookAtelierLuxe';
import BookPreviewFinalLuxe from './components/book/BookPreviewFinalLuxe';
import BookShareJoinLuxe from './components/contributeur/BookShareJoinLuxe';
import CollectiveParticipateLuxe from './components/contributeur/CollectiveParticipateLuxe';
import CollectiveJoinLuxe from './components/contributeur/CollectiveJoinLuxe';
import AccountSpaceLuxe from './components/account/AccountSpaceLuxe';
import BookCheckoutLuxe from './components/orders/BookCheckoutLuxe';
import OrdersLuxe from './components/orders/OrdersLuxe';
import MentionsLegalesLuxe from './components/legal/MentionsLegalesLuxe';
import CGVLuxe from './components/legal/CGVLuxe';
import ConfidentialiteLuxe from './components/legal/ConfidentialiteLuxe';
import TarifsLuxe from './components/legal/TarifsLuxe';
import ExemplesLuxe from './components/legal/ExemplesLuxe';





import ScrollToTop from './components/common/ScrollToTop';
import PageViewTracker from './components/common/PageViewTracker';
import PageMeta from './components/common/PageMeta';

// Ancienne page « Gérer le collectif » (refonte du collectif, phase 2) : le suivi vit dans l'atelier, panneau ouvert.
function RedirectionCollectif() {
  const { bookId } = useParams();
  return <Navigate to={`/book/${bookId}/atelier?collectif=1`} replace />;
}

// ============================================
// COMPOSANT DE ROUTE PROTÉGÉE
// ============================================
const ProtectedRoute = ({ children }) => {
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Attente : les deux billes Bookipix qui s'entrechoquent (BookipixLoader), plus de texte « Chargement... ».
  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <BookipixLoader size="lg" />
    </div>
  );
  
  return user ? children : <Navigate to="/login" />;
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================
function App() {
  React.useEffect(() => {
    // Retour d'une connexion OAuth : le composant de connexion n'est plus
    // monte, c'est donc ici qu'on rattache les livres commences en session
    // anonyme au compte qui vient de se connecter. Sans jeton anonyme en
    // attente, l'appel ne fait rien (voir services/anonymousSession.js).
    linkAnonymousBooksAfterLogin();
    // Premiere visite : prix et port affiches pour le pays d'arrivee (services/countryPreference.js).
    detectCountryOnFirstVisit(fetchDetectedCountry);
    // Chantier bilingue (2026-09-30) : synchronise la langue d'affichage
    // avec celle enregistree sur le compte, des la connexion (voir
    // services/languagePreference.js) — une seule fois pour toute
    // l'application, jamais par ecran.
    return initLanguagePreferenceSync();
  }, []);

  return (
    <Router>
      <ScrollToTop />
      <PageViewTracker />
      <PageMeta />
      <SiteModeGate>
      <Layout>
        <Routes>
  
          {/* ============================================
              PAGES PUBLIQUES
          ============================================ */}
          <Route path="/" element={<HomePageLuxe />} />
          <Route path="/how-it-works" element={<HowItWorksLuxe />} />
          <Route path="/faq" element={<FaqLuxe />} />
          {/* Pages legales/produit (2026-09-28) : les 7 liens du pied de page
              menaient tous a une redirection silencieuse vers l accueil
              (catch-all, voir plus bas) — voir memoire "commande-fiabilite..."
              et FooterLuxe.js pour le detail du bug de generation d URL. */}
          <Route path="/mentions-legales" element={<MentionsLegalesLuxe />} />
          <Route path="/cgv" element={<CGVLuxe />} />
          <Route path="/confidentialite" element={<ConfidentialiteLuxe />} />
          <Route path="/tarifs" element={<TarifsLuxe />} />
          <Route path="/exemples" element={<ExemplesLuxe />} />
          <Route path="/login" element={<LoginLuxe />} />
          <Route path="/register" element={<RegisterLuxe />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPasswordLuxe />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordLuxe />} />
          {/* Retrouver ses livres depuis un autre appareil : adresse + code,
              aucun mot de passe (voir services/emailOtp.js). */}
          <Route path="/mes-livres" element={<MesLivresLuxe />} />
          <Route path="/create-book" element={<CreateBookSansIA />} />
          {/* Espace d administration : la page se protege elle-meme (elle
              demande au serveur si l utilisateur y a droit) et le backend
              garde chaque route independamment. */}
          <Route path="/admin/*" element={<AdminApp />} />
		  <Route path="/participer/:token" element={<BookShareJoinLuxe />} />
		  <Route path="/collectif/:token" element={<CollectiveParticipateLuxe />} />
		  <Route path="/invitation/:token" element={<CollectiveJoinLuxe />} />



          {/* ============================================
              PAGES PROTÉGÉES
          ============================================ */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardGeneralLuxe />
            </ProtectedRoute>
          } />
          
          <Route path="/book/:bookId" element={
            <ProtectedRoute>
              <BookPageLuxe  />
            </ProtectedRoute>
          } />

          <Route path="/book/:bookId/composer" element={
            <ProtectedRoute>
              <BookComposeLuxe />
            </ProtectedRoute>
          } />

          <Route path="/book/:bookId/atelier" element={
            <ProtectedRoute>
              <BookAtelierLuxe />
            </ProtectedRoute>
          } />

          <Route path="/book/:bookId/apercu" element={
            <ProtectedRoute>
              <BookPreviewFinalLuxe />
            </ProtectedRoute>
          } />

          <Route path="/book/:bookId/collectif" element={<RedirectionCollectif />} />

          <Route path="/book/:bookId/checkout" element={
            <ProtectedRoute>
              <BookCheckoutLuxe />
            </ProtectedRoute>
          } />

          <Route path="/orders" element={
            <ProtectedRoute>
              <OrdersLuxe />
            </ProtectedRoute>
          } />

          <Route path="/account" element={
            <ProtectedRoute>
              <AccountSpaceLuxe />
            </ProtectedRoute>
          } />

          {/* ============================================
              REDIRECTION 404
          ============================================ */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Layout>
      </SiteModeGate>
    </Router>
  );
}

export default App;
